import { isPostalCodeRequired } from "@redotech/locale/countries";
import { shopifyProvinces } from "@redotech/locale/provinces";
import { useHandler } from "@redotech/react-util/hook";
import { t, useTranslation } from "@redotech/redo-i18n";
import {
  getCountryNameFromCode,
  RedoShopifyCountryCode,
} from "@redotech/redo-model/fulfillments/fulfillment-country-code";
import { FulfillmentOrderAddress } from "@redotech/redo-model/fulfillments/fulfillment-order-address";
import {
  groupInput,
  input,
  InputProvider,
  nonEmptyValidator,
  Validator,
} from "@redotech/ui/form";
import { memo, useMemo, useState } from "react";
import * as addressFormCss from "./address-autocomplete-form.module.css";
import { RedoCheckbox } from "./arbiter-components/checkbox/redo-checkbox";
import { LabelText } from "./arbiter-components/common/descriptors";
import { RedoTextInput } from "./arbiter-components/input/redo-text-input";
import { RedoSingleSelectDropdownInput } from "./arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { AutoCompleteInput } from "./autocomplete-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./components/ui/select";
import { Flex } from "./flex";
import { Text } from "./text";
import { SpacingValue } from "./theme/box";
import { isValidPhoneNumber } from "./utils/validators";

// Define the address type for better type safety
export interface AddressAutocompleteFields {
  address1: string;
  address2?: string;
  city: string;
  province: string;
  zip: string;
  countryCode: string;
  email?: string | null;
  company?: string | null;
  phone?: string | null;
}

// Define action handler interfaces for dependency injection
export interface AddressAutocompleteActions {
  getAddressSuggestionsFromAddressLine: (
    query: string,
    sessionToken?: string,
    countryCode?: string,
  ) => Promise<{ keyToFullObject: string; displayText: string }[]>;

  getAddressFromAddressLineSuggestion: (
    suggestionId: string,
  ) => Promise<AddressAutocompleteFields | null>;

  getAddressSuggestionsFromName: (
    nameSearchString: string,
  ) => Promise<
    { keyToFullObject: FulfillmentOrderAddress; displayText: string }[]
  >;

  getAddressFromNameSuggestion: (
    destinationAddress: FulfillmentOrderAddress,
  ) => Promise<AddressAutocompleteFields & { name: string }>;
}

const requiredPhoneValidator = (): Validator<string | null> => (value) => {
  if (!value) {
    return [t("Phone number is required")];
  }
  return isValidPhoneNumber(value) ? [] : [t("Invalid phone number")];
};

const phoneValidator = (): Validator<string | null> => (value) => {
  if (!value) {
    return [];
  }

  return isValidPhoneNumber(value) ? [] : [t("Invalid phone number")];
};

const emailValidator = (): Validator<string | null> => (value) => {
  if (!value) {
    return [t("Email is required")];
  }
  return [];
};

export const createAddressAutocompleteForm = (
  requireEmail = false,
  requirePhone = false,
) =>
  groupInput(
    {
      name: input<string>({ validator: nonEmptyValidator }),
      address1: input<string>({ validator: nonEmptyValidator }),
      address2: input<string | null>({}),
      city: input<string>({ validator: nonEmptyValidator }),
      company: input<string | null>({}),
      province: input<string | null>({}),
      zip: input<string>({}),
      countryCode: input<RedoShopifyCountryCode>({
        validator: nonEmptyValidator,
      }),
      phone: input<string | null>({
        validator: requirePhone ? requiredPhoneValidator() : phoneValidator(),
      }),
      email: input<string | null>({
        validator: requireEmail ? emailValidator() : undefined,
      }),
    },
    {
      validator: (value) => {
        if (
          isPostalCodeRequired(value.countryCode) &&
          !value.zip?.toString().trim()
        ) {
          return [t("Postal code is required")];
        }
        return [];
      },
    },
  );

export const omsAddressAutocompleteForm = createAddressAutocompleteForm(
  false,
  false,
);

export const addressAutocompleteForm = createAddressAutocompleteForm(false);

export type AddressAutocompleteFormType = InputProvider.Form<
  typeof addressAutocompleteForm
>;
export type AddressAutocompleteFormValue = InputProvider.Value<
  typeof addressAutocompleteForm
>;

export interface AddressAutocompleteFormProps {
  form: AddressAutocompleteFormType;
  title?: string;
  hideEmailField?: boolean;
  hideCompanyField?: boolean;
  includeNameField?: boolean;
  autoCompleteOnName?: boolean;
  actions?: AddressAutocompleteActions;
  requireEmail?: boolean;
  requirePhone?: boolean;
  showRequiredLabels?: boolean;
  gap?: SpacingValue;
  disabled?: boolean;
  useLegacyProvinceSelect?: boolean;
}

export const useAddressAutocomplete = (
  form: AddressAutocompleteFormType,
  actions?: AddressAutocompleteActions,
) => {
  const getAddressSuggestionsFromAddressLine = useHandler(
    async (query: string, sessionToken?: string) => {
      if (!actions?.getAddressSuggestionsFromAddressLine) {
        return [];
      }
      return await actions.getAddressSuggestionsFromAddressLine(
        query,
        sessionToken,
        form.value.countryCode,
      );
    },
  );

  const getAddressFromAddressLineSuggestion = useHandler(
    async (suggestionId: string) => {
      if (!actions?.getAddressFromAddressLineSuggestion) {
        return null;
      }
      return await actions.getAddressFromAddressLineSuggestion(suggestionId);
    },
  );

  const getAddressSuggestionsFromName = useHandler(
    async (nameSearchString: string) => {
      if (!actions?.getAddressSuggestionsFromName) {
        return [];
      }
      return await actions.getAddressSuggestionsFromName(nameSearchString);
    },
  );

  const getAddressFromNameSuggestion = useHandler(
    async (destinationAddress: FulfillmentOrderAddress) => {
      if (!actions?.getAddressFromNameSuggestion) {
        return {
          name: destinationAddress.name ?? "",
          address1: destinationAddress.address1 ?? "",
          address2: destinationAddress.address2 ?? "",
          city: destinationAddress.city ?? "",
          province: destinationAddress.province ?? "",
          zip: destinationAddress.zip ?? "",
          countryCode: destinationAddress.countryCode as RedoShopifyCountryCode,
          email: destinationAddress.email,
          company: destinationAddress.company,
          phone: destinationAddress.phone,
        };
      }
      return await actions.getAddressFromNameSuggestion(destinationAddress);
    },
  );

  // Google Places returns the 2-letter province code (e.g., "TX") for
  // administrative_area_level_1, but the dropdown options are keyed by full
  // province name (e.g., "Texas"). Normalize incoming values to the full name
  // so the dropdown can match.
  const normalizeProvince = (province: string, countryCode: string) => {
    const match = shopifyProvinces.find(
      (p) => p.countryCode === countryCode && p.provinceCode === province,
    );
    return match?.province ?? province;
  };

  const setAddressFields = useHandler((address: AddressAutocompleteFields) => {
    form.setValue({
      ...form.value,
      address1: address.address1,
      address2: address.address2 || null,
      city: address.city,
      province: normalizeProvince(address.province, address.countryCode),
      zip: address.zip,
      countryCode: address.countryCode as RedoShopifyCountryCode,
    });
  });

  const setAddressAndNameFields = useHandler(
    (fields: AddressAutocompleteFields & { name: string }) => {
      form.setValue({
        ...form.value,
        name: fields.name,
        address1: fields.address1,
        address2: fields.address2 || null,
        city: fields.city,
        province: normalizeProvince(fields.province, fields.countryCode),
        zip: fields.zip,
        countryCode: fields.countryCode as RedoShopifyCountryCode,
        email: fields.email ?? form.value.email,
        company: fields.company ?? form.value.company,
        phone: fields.phone ?? form.value.phone,
      });
    },
  );

  return {
    getAddressSuggestionsFromAddressLine,
    getAddressFromAddressLineSuggestion,
    getAddressSuggestionsFromName,
    getAddressFromNameSuggestion,
    setAddressFields,
    setAddressAndNameFields,
  };
};

export const AddressAutocompleteForm = memo(function AddressAutocompleteForm({
  form,
  title: tileProp,
  hideEmailField = false,
  hideCompanyField = false,
  includeNameField = true,
  autoCompleteOnName = true,
  actions,
  requireEmail = false,
  requirePhone = false,
  showRequiredLabels = false,
  gap = "3xl",
  disabled = false,
  useLegacyProvinceSelect = false,
}: AddressAutocompleteFormProps) {
  const { t } = useTranslation();
  const addressUtils = useAddressAutocomplete(form, actions);
  const [useManualProvinceEntry, setUseManualProvinceEntry] = useState(false);
  const [countrySearchString, setCountrySearchString] = useState("");
  const [provinceSearchString, setProvinceSearchString] = useState("");
  const title = tileProp ?? t("Recipient information");
  const requiredLabel = (label: string) =>
    showRequiredLabels ? `${label} *` : label;
  const nameLabel = requiredLabel(t("Name"));
  const countryLabel = requiredLabel(t("Country"));
  const addressLabel = requiredLabel(t("Address"));
  const cityLabel = requiredLabel(t("City"));
  const zipLabel = requiredLabel(t("Zip"));
  const phoneLabel = requirePhone
    ? showRequiredLabels
      ? requiredLabel(t("Phone number"))
      : t("Phone number (required)")
    : t("Phone number");
  const emailLabel = requireEmail
    ? requiredLabel(t("Email"))
    : t("Email (optional)");

  const {
    getAddressSuggestionsFromName,
    getAddressFromNameSuggestion,
    getAddressSuggestionsFromAddressLine,
    getAddressFromAddressLineSuggestion,
    setAddressFields,
    setAddressAndNameFields,
  } = addressUtils;

  const selectedCountryCode = useMemo(() => {
    return form.value.countryCode ?? RedoShopifyCountryCode.Us;
  }, [form.value.countryCode]);

  const availableProvinces = useMemo(() => {
    return shopifyProvinces.filter(
      (p) => p.countryCode === selectedCountryCode,
    );
  }, [selectedCountryCode]);

  const provinceSearchStringLower = provinceSearchString.trim().toLowerCase();
  const noneProvinceOption = {
    id: "none",
    type: "text" as const,
    text: t("None"),
    value: null,
  };
  const shouldShowNoneProvinceOption =
    !provinceSearchStringLower ||
    noneProvinceOption.text.toLowerCase().includes(provinceSearchStringLower);
  const noneProvinceOptions = shouldShowNoneProvinceOption
    ? [noneProvinceOption]
    : [];
  const matchingProvinceOptions = availableProvinces
    .filter(({ province }) =>
      province.toLowerCase().includes(provinceSearchStringLower),
    )
    .map(({ province }) => ({
      id: province,
      type: "text" as const,
      text: province,
      value: province,
    }));
  const provinceOptions = [...noneProvinceOptions, ...matchingProvinceOptions];

  return (
    <Flex dir="column" gap={gap}>
      {title && (
        <Text fontSize="md" fontWeight="semibold">
          {title}
        </Text>
      )}

      {includeNameField &&
        (autoCompleteOnName && actions && !disabled ? (
          <AutoCompleteInput<
            AddressAutocompleteFields & { name: string },
            FulfillmentOrderAddress
          >
            dataTestId="full-name-input"
            getSuggestions={getAddressSuggestionsFromName}
            label={nameLabel}
            pickSuggestion={getAddressFromNameSuggestion}
            placeholder={t("Enter name")}
            setAutocompleteFields={setAddressAndNameFields}
            setValue={(value) => {
              form.inputs.name.setValue(value);
            }}
            value={form.value.name}
          />
        ) : (
          <RedoTextInput
            dataTestId="full-name-input"
            label={nameLabel}
            placeholder={t("Enter name")}
            required
            setValue={form.inputs.name.setValue}
            state={disabled ? "disabled" : "default"}
            value={form.value.name}
          />
        ))}

      {!hideCompanyField && (
        <RedoTextInput
          dataTestId="address-company-input"
          label={t("Company")}
          placeholder={t("Enter company")}
          setValue={form.inputs.company.setValue}
          state={disabled ? "disabled" : "default"}
          value={form.value.company ?? ""}
        />
      )}

      <RedoSingleSelectDropdownInput<RedoShopifyCountryCode>
        dataTestId="country-input"
        disabled={disabled}
        label={countryLabel}
        options={Object.values(RedoShopifyCountryCode)
          .map((code) => ({
            id: code,
            type: "text" as const,
            text: getCountryNameFromCode(code),
            value: code,
          }))
          .filter(({ text }) =>
            text.toLowerCase().includes(countrySearchString.toLowerCase()),
          )
          .sort((a, b) => a.text.localeCompare(b.text))}
        optionSelected={({ item: country }) => {
          form.inputs.countryCode.setValue(country.value);
          form.inputs.province.setValue(null);
        }}
        searchPlaceholder={t("Search countries...")}
        searchString={countrySearchString}
        selectedItem={{
          id: selectedCountryCode,
          type: "text",
          text: getCountryNameFromCode(selectedCountryCode),
          value: selectedCountryCode,
        }}
        setSearchString={setCountrySearchString}
        size="xs"
      />

      {actions && !disabled ? (
        <AutoCompleteInput<AddressAutocompleteFields, string>
          dataTestId="address-street1-input"
          getSuggestions={getAddressSuggestionsFromAddressLine}
          label={addressLabel}
          pickSuggestion={getAddressFromAddressLineSuggestion}
          placeholder={t("Enter address")}
          setAutocompleteFields={setAddressFields}
          setValue={(value) => {
            form.inputs.address1.setValue(value);
          }}
          value={form.value.address1}
        />
      ) : (
        <RedoTextInput
          dataTestId="address-street1-input"
          label={addressLabel}
          placeholder={t("Enter address")}
          setValue={form.inputs.address1.setValue}
          state={disabled ? "disabled" : "default"}
          value={form.value.address1}
        />
      )}

      <RedoTextInput
        dataTestId="address-street2-input"
        label={t("Apartment, suite, etc. (optional)")}
        placeholder={t("Enter your number")}
        setValue={form.inputs.address2.setValue}
        state={disabled ? "disabled" : "default"}
        value={form.value.address2 ?? ""}
      />

      <Flex dir="row" w="full">
        <Flex
          basis="0"
          className={addressFormCss.equalColumn}
          grow={1}
          shrink={1}
        >
          <RedoTextInput
            dataTestId="city-input"
            fullWidth
            label={cityLabel}
            placeholder={t("Enter your city")}
            setValue={form.inputs.city.setValue}
            value={form.value.city}
          />
        </Flex>
        <Flex
          basis="0"
          className={addressFormCss.equalColumn}
          dir="column"
          gap="sm"
          grow={1}
          shrink={1}
        >
          {useLegacyProvinceSelect ? (
            <>
              <Flex align="center" dir="row" justify="space-between">
                <LabelText
                  label={
                    selectedCountryCode === RedoShopifyCountryCode.Us
                      ? t("State")
                      : t("Province")
                  }
                  size="xs"
                />
                {availableProvinces.length > 0 && (
                  <RedoCheckbox
                    disabled={disabled}
                    label={t("Enter manually")}
                    setValue={(value) =>
                      setUseManualProvinceEntry(value === true)
                    }
                    size="xs"
                    value={useManualProvinceEntry}
                  />
                )}
              </Flex>
              {useManualProvinceEntry || availableProvinces.length === 0 ? (
                <RedoTextInput
                  dataTestId="state-input"
                  placeholder={
                    selectedCountryCode === RedoShopifyCountryCode.Us
                      ? t("Enter state")
                      : t("Enter province")
                  }
                  setValue={form.inputs.province.setValue}
                  state={disabled ? "disabled" : "default"}
                  value={form.value.province ?? ""}
                />
              ) : (
                <RedoSingleSelectDropdownInput<string | null>
                  dataTestId="state-input"
                  disabled={disabled}
                  emptyListMessage={t("No results")}
                  options={provinceOptions}
                  optionSelected={({ item: province }) => {
                    form.inputs.province.setValue(province.value);
                  }}
                  searchPlaceholder={
                    selectedCountryCode === RedoShopifyCountryCode.Us
                      ? t("Search states...")
                      : t("Search provinces...")
                  }
                  searchString={provinceSearchString}
                  selectedItem={
                    form.value.province
                      ? {
                          id: form.value.province,
                          type: "text" as const,
                          text: form.value.province,
                          value: form.value.province,
                        }
                      : noneProvinceOption
                  }
                  setSearchString={setProvinceSearchString}
                  size="xs"
                />
              )}
            </>
          ) : (
            <>
              <Flex align="center" dir="row" justify="space-between">
                <LabelText
                  label={
                    selectedCountryCode === RedoShopifyCountryCode.Us
                      ? t("State")
                      : t("Province")
                  }
                  size="xs"
                />
                {availableProvinces.length > 0 && (
                  <RedoCheckbox
                    label={t("Enter manually")}
                    setValue={(value) =>
                      setUseManualProvinceEntry(value === true)
                    }
                    size="xs"
                    value={useManualProvinceEntry}
                  />
                )}
              </Flex>
              {useManualProvinceEntry || availableProvinces.length === 0 ? (
                <RedoTextInput
                  dataTestId="state-input"
                  placeholder={
                    selectedCountryCode === RedoShopifyCountryCode.Us
                      ? t("Enter state")
                      : t("Enter province")
                  }
                  setValue={form.inputs.province.setValue}
                  state={disabled ? "disabled" : "default"}
                  value={form.value.province ?? ""}
                />
              ) : (
                <Select
                  onValueChange={(value) => {
                    form.inputs.province.setValue(
                      value === "none" ? null : value,
                    );
                  }}
                  value={form.value.province ?? "none"}
                >
                  <SelectTrigger data-testid="state-input" variant="redo">
                    <SelectValue
                      placeholder={
                        selectedCountryCode === RedoShopifyCountryCode.Us
                          ? t("Select state")
                          : t("Select province")
                      }
                    />
                  </SelectTrigger>
                  <SelectContent variant="redo">
                    <SelectItem
                      data-testid="dropdown-option-None"
                      value="none"
                      variant="redo"
                    >
                      {t("None")}
                    </SelectItem>
                    {availableProvinces.map(({ province }) => (
                      <SelectItem
                        data-testid={`dropdown-option-${province}`}
                        key={province}
                        value={province}
                        variant="redo"
                      >
                        {province}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </>
          )}
        </Flex>
      </Flex>

      <RedoTextInput
        dataTestId="zip-input"
        label={zipLabel}
        placeholder={t("Enter your zip")}
        setValue={form.inputs.zip.setValue}
        state={disabled ? "disabled" : "default"}
        value={form.value.zip}
      />

      <RedoTextInput
        dataTestId="phone-input"
        errorText={
          requirePhone && form.changed && form.inputs.phone.allErrors.length > 0
            ? form.inputs.phone.allErrors.join(", ")
            : undefined
        }
        label={phoneLabel}
        placeholder={
          requirePhone
            ? t("Enter phone number (required)")
            : t("Enter phone number (optional)")
        }
        setValue={form.inputs.phone.setValue}
        state={disabled ? "disabled" : "default"}
        value={form.value.phone ?? ""}
      />

      {!hideEmailField && (
        <RedoTextInput
          dataTestId="email-input"
          errorText={form.inputs.email.allErrors?.[0]}
          label={emailLabel}
          placeholder={requireEmail ? "*" : ""}
          setValue={form.inputs.email.setValue}
          state={disabled ? "disabled" : "default"}
          value={form.value.email ?? ""}
        />
      )}
    </Flex>
  );
});
